/* eslint-disable camelcase */
import React, { useState, useEffect } from "react"
import { ThemeProvider, createTheme } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Tooltip,
  IconButton,
  Alert,
} from "@mui/material"
import {
  Gavel,
  PictureAsPdf,
  TableView,
  FilterList,
  Search,
  Visibility,
  WarningAmber,
  Public,
  CloudOff,
  CloudDone,
} from "@mui/icons-material"
import Swal from "sweetalert2"
import localforage from "localforage"

import Header from "../../../../layouts/Header"
import BackIcon from "../../../../components/BackIcon"
import { api } from "../../../../api"

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#ed6c02" },
    success: { main: "#2E7D32" },
    error: { main: "#d32f2f" },
  },
})

const ReportesExportacionLegal = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine)
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [errorAcceso, setErrorAcceso] = useState(null)

  const [esGlobal, setEsGlobal] = useState(false)
  const [cobertura, setCobertura] = useState("Cargando...")
  const [listaCantones, setListaCantones] = useState([])

  const [filtroCanton, setFiltroCanton] = useState("TODOS")
  const [filtroEstado, setFiltroEstado] = useState("NOVEDAD")
  const [filtroBusqueda, setFiltroBusqueda] = useState("")

  const [actas, setActas] = useState([])

  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)
    window.addEventListener("online", handleOnline)
    window.addEventListener("offline", handleOffline)
    return () => {
      window.removeEventListener("online", handleOnline)
      window.removeEventListener("offline", handleOffline)
    }
  }, [])

  useEffect(() => {
    cargarActasLegales()
  }, [filtroCanton, filtroEstado])

  useEffect(() => {
    if (esGlobal && !isOffline) {
      const fetchCantones = async () => {
        try {
          const res = await api.post("/ControlElectoral/getFiltrosGeografia", { tipo: "CANTONES" })
          if (res.data?.success) setListaCantones(res.data.data)
        } catch (e) {
          console.error("No se cargaron los cantones.")
        }
      }
      fetchCantones()
    }
  }, [esGlobal, isOffline])

  const cargarActasLegales = async () => {
    setIsLoading(true)
    setErrorAcceso(null)
    try {
      if (isOffline) throw new Error("Offline")

      const payload = { canton: filtroCanton, estado: filtroEstado }
      const res = await api.post("/ControlElectoral/getReportesLegales", payload)
      const data = res?.data !== undefined ? res.data : res

      if (data && data.success === false) {
        setErrorAcceso(data.message)
        return
      }

      setActas(data.data || [])
      setEsGlobal(data.es_global)
      setCobertura(data.cobertura)

      await localforage.setItem("reportes_legales_cache", data.data || [])
      await localforage.setItem("reportes_cobertura_cache", data.cobertura)
      await localforage.setItem("reportes_global_cache", data.es_global)
    } catch (error) {
      if (error?.response?.status === 403) {
        setErrorAcceso(error.response.data.message || "Usuario no autorizado.")
      } else {
        const cache = (await localforage.getItem("reportes_legales_cache")) || []
        const cCache = (await localforage.getItem("reportes_cobertura_cache")) || "Caché Offline"
        const gCache = (await localforage.getItem("reportes_global_cache")) || false

        setActas(cache)
        setCobertura(cCache)
        setEsGlobal(gCache)
      }
    } finally {
      setIsLoading(false)
    }
  }

  // Búsqueda en vivo
  const actasFiltradas = actas.filter(
    (a) =>
      a.acta_codigo.toLowerCase().includes(filtroBusqueda.toLowerCase()) ||
      a.recinto.toLowerCase().includes(filtroBusqueda.toLowerCase()),
  )

  // ========================================================
  // DESCARGA BINARIA DEL EXCEL Y PDF
  // ========================================================
  const handleExportar = async (formato) => {
    if (isOffline) {
      return Swal.fire("Sin Conexión", "Debe estar conectado para generar los archivos.", "error")
    }
    if (actasFiltradas.length === 0) {
      return Swal.fire("Tabla Vacía", "No hay registros para exportar.", "warning")
    }

    setIsExporting(true)
    try {
      const endpoint =
        formato === "EXCEL"
          ? "/ControlElectoral/exportarReportesLegalesExcel"
          : "/ControlElectoral/exportarReportesLegalesPDF"

      // 1. Usamos tu API original para que los tokens JWT viajen automáticamente.
      // Le decimos a Axios que espere un binario (blob) y no un JSON.
      const res = await api.post(endpoint, { datos: actasFiltradas }, { responseType: "blob" })

      // 2. Extracción inteligente: Tu wrapper puede devolver el Blob directo o dentro de res.data.
      const archivoCrudo = res instanceof Blob ? res : res?.data instanceof Blob ? res.data : res?.data || res

      // 3. Tipos MIME oficiales para reconstruir el archivo sin corromperlo
      const extension = formato === "EXCEL" ? "xlsx" : "pdf"
      const mimeType =
        formato === "EXCEL" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "application/pdf"

      const blobFinal = new Blob([archivoCrudo], { type: mimeType })

      // 4. Descarga forzada en el navegador
      const url = window.URL.createObjectURL(blobFinal)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `Matriz_Impugnaciones_${new Date().getTime()}.${extension}`)

      document.body.appendChild(link)
      link.click()

      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)

      Swal.fire({ icon: "success", title: `${formato} Generado`, confirmButtonColor: "#196C87" })
    } catch (error) {
      console.error("Error de exportación:", error)
      Swal.fire("Error del Servidor", "No se pudo generar el documento.", "error")
    } finally {
      setIsExporting(false)
    }
  }

  // ========================================================
  // CORRECCIÓN C.O.R.S. PARA FOTOGRAFÍAS
  // ========================================================
  const verFotoActa = (foto_url) => {
    if (!foto_url) return Swal.fire("Sin evidencia", "Esta acta no tiene fotografía adjunta.", "warning")

    const nombreArchivo = foto_url.split("/").pop()

    // Fallback dinámico: Usa tu variable de entorno o el puerto quemado de desarrollo
    const backendOrigin = process.env.REACT_APP_API_URL || "http://localhost:5000"
    const urlAbsoluta = `${backendOrigin}/ControlElectoral/archivos/evidencia/${nombreArchivo}`

    Swal.fire({
      imageUrl: urlAbsoluta,
      imageAlt: "Evidencia Fotográfica",
      width: "80%",
      showConfirmButton: false,
      showCloseButton: true,
    })
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-4" style={{ backgroundColor: "#eef2f6", minHeight: "100vh" }}>
        <BackIcon />

        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "flex-end" }}
          mb={3}
          gap={2}
        >
          <Box>
            <Typography variant="h4" fontWeight="900" color="primary.main" display="flex" alignItems="center">
              <Gavel sx={{ fontSize: 40, mr: 1 }} /> Exportación Legal e Impugnaciones
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" fontWeight="bold">
              Generación de respaldos para presentación ante el CNE
            </Typography>
          </Box>

          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Chip
              icon={isOffline ? <CloudOff /> : <CloudDone />}
              label={isOffline ? "OFFLINE" : "EN LÍNEA"}
              color={isOffline ? "error" : "success"}
              sx={{ fontWeight: "bold" }}
            />
            <Button
              variant="contained"
              sx={{ bgcolor: "#2E7D32", "&:hover": { bgcolor: "#1b5e20" }, fontWeight: "bold" }}
              startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <TableView />}
              onClick={() => handleExportar("EXCEL")}
              disabled={isExporting || isOffline}
            >
              MATRIZ EXCEL
            </Button>
            <Button
              variant="contained"
              sx={{ bgcolor: "#d32f2f", "&:hover": { bgcolor: "#c62828" }, fontWeight: "bold" }}
              startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <PictureAsPdf />}
              onClick={() => handleExportar("PDF")}
              disabled={isExporting || isOffline}
            >
              CARPETA PDF
            </Button>
          </Box>
        </Box>

        {errorAcceso ? (
          <Alert severity="error" variant="filled" sx={{ borderRadius: 2, p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              ACCESO RESTRINGIDO
            </Typography>
            <Typography variant="body1">{errorAcceso}</Typography>
          </Alert>
        ) : (
          <>
            <Box display="flex" alignItems="center" mb={3} p={1.5} bgcolor="#e1f5fe" borderRadius={2}>
              <Typography variant="subtitle2" color="primary.dark" fontWeight="bold" display="flex" alignItems="center">
                📍 Cobertura: {cobertura}
              </Typography>
              {esGlobal && (
                <Chip
                  icon={<Public fontSize="small" />}
                  label="MODO GLOBAL"
                  color="info"
                  size="small"
                  sx={{ ml: 2, fontWeight: "bold" }}
                />
              )}
            </Box>

            <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3, borderTop: "4px solid #196C87" }}>
              <Box display="flex" alignItems="center" mb={2}>
                <FilterList color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Filtros de Extracción
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {esGlobal && (
                  <Grid item xs={12} md={3}>
                    <TextField
                      select
                      fullWidth
                      label="Territorio (Cantón)"
                      value={filtroCanton}
                      onChange={(e) => setFiltroCanton(e.target.value)}
                      size="small"
                      disabled={isOffline}
                    >
                      <MenuItem value="TODOS">-- Todos los Cantones --</MenuItem>
                      {listaCantones.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}

                <Grid item xs={12} md={esGlobal ? 3 : 4}>
                  <TextField
                    select
                    fullWidth
                    label="Estado Jurídico del Acta"
                    value={filtroEstado}
                    onChange={(e) => setFiltroEstado(e.target.value)}
                    size="small"
                    disabled={isOffline}
                  >
                    <MenuItem value="TODOS">-- Todas las Actas --</MenuItem>
                    <MenuItem value="CUADRADA">Válidas (Cuadradas)</MenuItem>
                    <MenuItem value="NOVEDAD">Con Novedad (Para revisión)</MenuItem>
                    <MenuItem value="IMPUGNADA">Ya Impugnadas</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={esGlobal ? 6 : 8}>
                  <TextField
                    fullWidth
                    placeholder="Buscar por código de acta o recinto..."
                    value={filtroBusqueda}
                    onChange={(e) => setFiltroBusqueda(e.target.value)}
                    size="small"
                    InputProps={{ startAdornment: <Search color="action" sx={{ mr: 1 }} /> }}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={3} sx={{ borderRadius: 3, overflow: "hidden" }}>
              <TableContainer sx={{ maxHeight: "55vh" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>Código Acta</TableCell>
                      <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>Ubicación (Recinto / Junta)</TableCell>
                      <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>Delegado</TableCell>
                      <TableCell align="center" sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>
                        Padrón vs Ingreso
                      </TableCell>
                      <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>Motivo Jurídico</TableCell>
                      <TableCell align="center" sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>
                        Estado
                      </TableCell>
                      <TableCell align="center" sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>
                        Evidencia
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                          <CircularProgress />
                        </TableCell>
                      </TableRow>
                    ) : actasFiltradas.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                          <Typography color="textSecondary">
                            No se encontraron actas bajo estos criterios legales.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      actasFiltradas.map((acta) => {
                        let chipColor = "success"
                        let rowBg = "inherit"
                        if (acta.estado === "NOVEDAD") {
                          chipColor = "error"
                          rowBg = "#fff8e1"
                        }
                        if (acta.estado === "IMPUGNADA") {
                          chipColor = "secondary"
                          rowBg = "#ffebee"
                        }

                        return (
                          <TableRow key={acta.acta_codigo} hover sx={{ bgcolor: rowBg }}>
                            <TableCell sx={{ fontWeight: "bold", color: "#196C87" }}>{acta.acta_codigo}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="bold">
                                {acta.recinto}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {acta.junta}
                              </Typography>
                            </TableCell>
                            <TableCell>{acta.delegado}</TableCell>
                            <TableCell align="center">
                              <Typography
                                variant="body2"
                                color={acta.votos_ingresados > acta.padron ? "error.main" : "textPrimary"}
                                fontWeight="bold"
                              >
                                {acta.votos_ingresados} / {acta.padron}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {acta.estado !== "CUADRADA" ? (
                                <Box display="flex" alignItems="center" color="error.main">
                                  <WarningAmber sx={{ fontSize: 16, mr: 0.5 }} />
                                  <Typography variant="caption" fontWeight="bold">
                                    {acta.motivo_alerta}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="textSecondary">
                                  Sin observaciones
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={acta.estado} size="small" color={chipColor} sx={{ fontWeight: "bold" }} />
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Ver Fotografía Original">
                                <IconButton color="primary" size="small" onClick={() => verFotoActa(acta.foto_url)}>
                                  <Visibility fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        )
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </div>
    </ThemeProvider>
  )
}

export default ReportesExportacionLegal
